import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/dad850ptto0ceqqngcbg';

const footerLinks = [
  { label: '公司', href: '/about/company' },
  { label: '投资关系', href: '/about/investor-relations' },
  { label: '相关资料', href: '/about/resources' },
  { label: '产品服务', href: '/products-services' },
  { label: '解决方案', href: '/solutions' },
  { label: '行业咨询', href: '/industry-consulting' },
  { label: '客户案例', href: '/cases' },
  { label: '业务联系', href: '/contact/business' },
  { label: '项目演示', href: '/demo' },
];

export default function Footer() {
  return (
    <footer className="bg-foreground-950 text-background-100">
      <ContactSection />
      <div className="w-full px-4 md:px-6 py-10 border-t border-foreground-800/60">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                  <i className="ri-cpu-line text-background-50 text-lg"></i>
                </div>
                <div>
                  <div className="font-heading font-bold">华辉数智技术</div>
                  <div className="text-[10px] tracking-widest text-foreground-400">
                    HUAHUI DIGITAL INTELLIGENCE
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-foreground-400 leading-relaxed">
                人工智能应用服务 · 乡村数字经济 · 县域数字商业基础设施
              </p>
            </div>

            <div>
              <div className="text-sm font-medium text-background-50 mb-3">快速导航</div>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {[...footerLinks].map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-sm text-foreground-400 hover:text-background-50 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-background-50 mb-3">战略标签</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 text-xs rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30 whitespace-nowrap">
                  AI × 数字商业 × 乡村振兴
                </span>
                <span className="px-3 py-1 text-xs rounded-full bg-accent-500/20 text-accent-300 border border-accent-500/30 whitespace-nowrap">
                  人工智能应用服务商
                </span>
              </div>
            </div>
          </div>

          <ComplianceNote />

          <div className="mt-8 pt-6 border-t border-foreground-800/60 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-foreground-500">
              © {new Date().getFullYear()} 华辉数智技术 HUAHUI DIGITAL INTELLIGENCE. 保留所有权利。
            </p>
            <p className="text-xs text-foreground-500">让 AI 进入县域，让数字连接乡村</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ComplianceNote() {
  return (
    <div className="mt-8 p-5 rounded-lg bg-foreground-900/60 border border-foreground-800">
      <div className="flex items-center gap-2 mb-3">
        <i className="ri-shield-check-line text-accent-400"></i>
        <span className="text-sm font-medium text-background-50">项目说明与合规声明</span>
      </div>
      <ul className="space-y-2 text-xs text-foreground-400 leading-relaxed">
        <li>
          九畴云市相关战略规划、市场规模、网络数量及未来发展目标属于项目规划或发展目标，实际实施情况以项目运营数据及正式公告为准。
        </li>
        <li>
          官网所展示的人工智能应用服务商、服务团、相关政策申报等内容，属于项目对接、规划或申报方向；取得相关资质、入库或认定后方可使用相应官方身份表述。
        </li>
        <li>
          数字人民币相关内容仅指数字人民币应用场景及相关技术服务，不代表华辉数智从事数字人民币发行或金融机构业务。
        </li>
        <li>本项目内容不构成任何投资建议。</li>
      </ul>
    </div>
  );
}

function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐校验：命中则静默返回成功
    const hp = String(fd.get('company_alt') || '').trim();
    if (hp) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === 'string' && key !== 'company_alt') {
        params.append(key, value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后重试');
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-14">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h4 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
            业务联系
          </h4>
          <p className="mt-3 text-sm text-foreground-400 leading-relaxed">
            欢迎投资人、产业合作伙伴、县域商业从业者与政策相关部门与我们联系，共同参与九畴云市乡村数字经济操作系统建设。
          </p>
          <div className="mt-6 space-y-4 text-sm text-foreground-300">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-500/20 flex items-center justify-center">
                <i className="ri-mail-line text-primary-300"></i>
              </div>
              <span>huahuitechnology@hhsztech.cn</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent-500/20 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-accent-300"></i>
              </div>
              <span>深圳市宝安区西乡街道蚝业社区金港大厦金港中心A座603（前海合作区）</span>
            </div>
          </div>
        </div>

        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="p-6 rounded-lg bg-foreground-900/60 border border-foreground-800 space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-foreground-400 mb-1.5">姓名</label>
              <input
                name="name"
                type="text"
                required
                className="w-full px-3 py-2.5 rounded-md bg-foreground-950/60 border border-foreground-700 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
                placeholder="您的姓名"
              />
            </div>
            <div>
              <label className="block text-xs text-foreground-400 mb-1.5">邮箱</label>
              <input
                name="email"
                type="email"
                required
                className="w-full px-3 py-2.5 rounded-md bg-foreground-950/60 border border-foreground-700 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-foreground-400 mb-1.5">合作类型</label>
            <select
              name="cooperation_type"
              className="w-full px-3 py-2.5 rounded-md bg-foreground-950/60 border border-foreground-700 text-sm text-background-50 focus:outline-none focus:border-primary-400"
            >
              <option value="投资">投资</option>
              <option value="产业合作">产业合作</option>
              <option value="县域商业">县域商业</option>
              <option value="政策对接">政策对接</option>
              <option value="其他">其他</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-foreground-400 mb-1.5">留言</label>
            <textarea
              name="message"
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-md bg-foreground-950/60 border border-foreground-700 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 resize-none"
              placeholder="请简要说明您的需求（500 字以内）"
            />
          </div>

          {/* 反垃圾蜜罐字段 */}
          <input
            type="text"
            name="company_alt"
            className="form-hp-guard"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {status === 'loading' ? '提交中...' : '提交联系信息'}
          </button>

          {status === 'success' && (
            <p className="text-sm text-accent-400">提交成功，我们会尽快与您联系。</p>
          )}
          {status === 'error' && (
            <p className="text-sm text-red-400">{errorMsg}</p>
          )}
        </form>
      </div>
    </div>
  );
}